import {TableColumn, TableExpandedRows} from 'src/components/table/Table.tsx'
import {StyledDesktopTBody, StyledTCell, StyledTRowWrapper, StyledTRow} from 'src/components/table/desktop-t-body/style'
import {DesktopSkeleton} from 'src/components/table/desktop-skeleton/DesktopSkeleton'
import {EmptyPlaceholder} from 'src/components/table/empty-placeholder/EmptyPlaceholder'
import {ErrorMessage} from 'src/components/table/error-message/ErrorMessage'
import {ReactNode} from 'react'

interface DesktopTBodyProps<TData extends unknown[], TSortName extends string | undefined> {
    columns: TableColumn<TData, TSortName>[]
    data: TData
    isError?: boolean
    expandedRows?: TableExpandedRows<TData>
    isChangingPage?: boolean
    isLoading: boolean
    onChangePage?: (lastItemIndex: number) => void
    subRow?: (item: TData[number], itemIndex: number) => ReactNode
    variant?: 'neutral' | 'default'
}

export const DesktopTBody = <TData extends unknown[], TSortName extends string | undefined>({
    columns,
    data,
    isError,
    expandedRows,
    isChangingPage,
    isLoading,
    onChangePage,
    subRow,
    variant
}: DesktopTBodyProps<TData, TSortName>) => {
    return (
        <StyledDesktopTBody
            $variant={variant}
            data={data}
            endReached={isError || isLoading || isChangingPage ? undefined : onChangePage}
            increaseViewportBy={200}
            overscan={100}
            itemContent={(itemIndex, item) => {
                return (
                    <StyledTRowWrapper key={itemIndex}>
                        <StyledTRow>
                            {columns.map((column, index) => (
                                <StyledTCell $alignment={column.alignment} $width={column.width} key={index}>
                                    <div>{column.cellRender(item, itemIndex)}</div>
                                </StyledTCell>
                            ))}
                        </StyledTRow>
                        {subRow?.(item, itemIndex)}
                        {expandedRows?.indexes?.includes(itemIndex) && expandedRows.render(item, itemIndex)}
                    </StyledTRowWrapper>
                )
            }}
            components={{
                EmptyPlaceholder: () => {
                    if (isLoading) {
                        return <DesktopSkeleton columns={columns} />
                    }
                    if (isError) {
                        return null
                    }

                    return <EmptyPlaceholder />
                },
                Footer: () => {
                    if (isLoading) {
                        return null
                    }
                    if (isError) {
                        return <ErrorMessage />
                    }
                    if (isChangingPage) {
                        return <DesktopSkeleton columns={columns} />
                    }

                    return null
                }
            }}
        />
    )
}

DesktopTBody.displayName = 'DesktopTBody'
